import { RestClient, RestClientOptions } from "libs/okx-api/src";
import { MarketError } from "../error/codes";
import { firstValueFrom, tap, defer, throwError, catchError } from "rxjs";
import { MarketLoggerService, RestRequestLogRow } from "./market-logger.service";
import { isEmpty } from "lodash-es";
import { AxiosRequestConfig } from "axios";
import { OkxTimeService } from "./market-time.service";

export class OkxRestClient extends RestClient {

  static timeService?: OkxTimeService

  constructor(
    restClientOptions: RestClientOptions = {},
    requestOptions: AxiosRequestConfig = {},
    private marketLogger: MarketLoggerService,
  ) {
    super(null, 'prod', restClientOptions, requestOptions)
  }

  /**
   * Логирует ответ от биржи в market log
   */
  private logResponse(url: string, params: any, response: any) {
    const {url: parsedUrl, params: queryParams} = this.parseUrl(url)
    params = typeof(params) == 'object' ? params : {}
    const data = {...queryParams, ...params, signature: undefined, timestamp: undefined}
    const logRow: RestRequestLogRow = {
      time: Date.now(),
      market: 'okx',
      type: 'rest',
      url: parsedUrl,
      params: isEmpty(data) ? undefined : data,
      httpCode: 200,
      apiCode: response.code,
      apiMessage: response.msg
    }
    if (this.logFilter(logRow)) {
      this.marketLogger.log(logRow)
    }
  }

  /**
   * Логирует ошибку от биржи в weblog
   */
  private logError(e: any) {
    if (e && this.logFilter(e)) {
      // Это ошибка от биржи сконвертированная либой
      if ('requestUrl' in e && 'httpStatus' in e && 'body' in e) {
        const {url: parsedUrl, params: queryParams} = this.parseUrl(e.requestUrl)
        const bodyParams = typeof(e.requestBody) == 'object' ? e.requestBody : {}
        const data = {...queryParams, ...bodyParams, signature: undefined, timestamp: undefined}
        const logRow: RestRequestLogRow = {
          time: Date.now(),
          market: 'okx' as any,
          type: 'rest',
          url: parsedUrl,
          params: isEmpty(data) ? undefined : data,
          httpCode: e.httpStatus,
          apiCode: e.body.code,
          apiMessage: e.body.msg
        }
        this.marketLogger.log(logRow)
      }
    }
  }

  /**
   * Конвертирует ошибку биржи в MarketError
   */
  private convertError(e: any): any {
    if ('body' in e && 'code' in e.body && 'msg' in e.body) {
      // Это ошибка api okx
      if ('data' in e.body && e.body.data[0] && 'sCode' in e.body.data[0] && 'sMsg' in e.body.data[0]) {
        return new MarketError(e.body.data[0].sCode, e.body.data[0].sMsg, e.body.data)
      }
      return new MarketError(e.body.code, e.body.msg)
    }
    return e
  }

  /**
   * Фильтрует ошибку для логирования в weblog
   */
  private logFilter(e: any) {
    const url = typeof(e.requestUrl) == 'string' ? e.requestUrl : ''
    const body = typeof(e.requestBody) == 'object' ? e.requestBody : {}
    if (url.includes('/api/v5/trade/cancel-order') && body.instId == 'BTC-USDT-SWAP' && body.ordId == '0') {
      // Такой запрос используется для проверки ключа, не нужно его логировать
      return false
    }
    return true
  }

  private parseUrl(url: string): {url: string, params: Record<string, any> | undefined} {
    try {
      const objUrl = new URL(url)
      const queryParams = Object.fromEntries(objUrl.searchParams.entries())
      return {url: objUrl.origin + objUrl.pathname, params: queryParams}
    } catch {
      return {url: url, params: undefined}
    }
  }

  private executeRequest(endpoint: string, params: any, callback: () => Promise<any>): Promise<any> {
    return firstValueFrom(
      defer(() => callback()).pipe(
        tap(response => this.logResponse(endpoint, params, response)),
        catchError(error => {
          this.logError(error)
          return throwError(() => this.convertError(error))
        })
      )
    )
  }

  override async get(endpoint: string, params?: any) {
    return await this.executeRequest(endpoint, params, () => super.get(endpoint, params))
  }

  override async post(endpoint: string, params?: any) {
    return await this.executeRequest(endpoint, params, () => super.post(endpoint, params))
  }

  override async getPrivate(endpoint: string, params?: any) {
    return await this.executeRequest(endpoint, params, () => super.getPrivate(endpoint, params))
  }

  override async postPrivate(endpoint: string, params?: any) {
    return await this.executeRequest(endpoint, params, () => super.postPrivate(endpoint, params))
  }

  override async deletePrivate(endpoint: string, params?: any) {
    return await this.executeRequest(endpoint, params, () => super.deletePrivate(endpoint, params))
  }

}
